import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, ChevronDown } from 'lucide-react';
const faqs = [{
  question: 'How quickly can you onboard our claims?',
  answer: 'Our implementation team can typically onboard new clients within 30 to 45 days. We use a structured, technology-driven process to ensure a seamless transition without disrupting ongoing care for injured workers.'
}, {
  question: 'Do you work with our existing claims system?',
  answer: 'Yes. Arissa Cost Solutions is system-agnostic. Our technology platform integrates seamlessly with all major claims management systems (CMS) via secure API or EDI connections, ensuring real-time data exchange.'
}, {
  question: 'What states do you operate in?',
  answer: "We are a national provider with a comprehensive 50-state network. We combine this national reach with deep jurisdictional expertise to ensure compliance with local workers' compensation regulations everywhere you operate."
}, {
  question: 'How is your A+ Ancillary Services different?',
  answer: 'Unlike traditional models that fragment care, our A+ Ancillary Services provide a single point of contact for physical therapy, diagnostics, DME, and transportation. This unified approach reduces delays in care and accelerates return-to-work timelines.'
}, {
  question: 'What does pricing look like?',
  answer: 'We offer transparent, performance-based pricing models tailored to your specific program needs. Whether you prefer flat-fee arrangements or percentage-of-savings models, we align our success directly with your financial outcomes.'
}, {
  question: 'How do you measure outcomes?',
  answer: 'We track both financial and clinical metrics, including average claim cost, network penetration, return-to-work duration, and patient satisfaction. Clients receive customized dashboards for real-time visibility into program performance.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-24 bg-white border-t border-gray-100" data-id="element-41">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-42">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8" data-id="element-43">
          {/* Left Column: Intro */}
          <div className="lg:col-span-4" data-id="element-44">
            <div className="sticky top-32" data-id="element-45">
              <div className="w-12 h-12 bg-brand-gold/10 rounded-xl flex items-center justify-center mb-6 text-brand-gold" data-id="element-46">
                <HelpCircle className="w-6 h-6" data-id="element-47" />
              </div>
              <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-48">
                Common Questions
              </h2>
              <h3 className="text-3xl md:text-4xl font-serif text-brand-navy mb-6" data-id="element-49">
                Everything you need to know
              </h3>
              <p className="text-gray-600 leading-relaxed mb-8" data-id="element-50">
                Evaluating a new managed care partner is a major decision. Here
                are answers to the most common questions we hear from carriers,
                TPAs, and employers.
              </p>
              <a href="#contact" className="text-brand-navy font-semibold hover:text-brand-gold transition-colors inline-flex items-center" data-id="element-51">
                Have more questions? Contact us &rarr;
              </a>
            </div>
          </div>

          {/* Right Column: Accordion */}
          <div className="lg:col-span-8" data-id="element-52">
            <div className="space-y-4" data-id="element-53">
              {faqs.map((faq, index) => {
              const isOpen = openIndex === index;
              return <motion.div key={index} initial={{
                opacity: 0,
                y: 10
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: index * 0.1
              }} className={`border rounded-xl overflow-hidden transition-colors duration-300 ${isOpen ? 'border-brand-gold/50 bg-brand-paper/50' : 'border-gray-200 bg-white'}`} data-id="element-54">
                    <button onClick={() => setOpenIndex(isOpen ? null : index)} className="w-full flex items-center justify-between p-6 text-left focus:outline-none" data-id="element-55">
                      <span className="font-serif font-bold text-lg text-brand-navy pr-8" data-id="element-56">
                        {faq.question}
                      </span>
                      <ChevronDown className={`w-5 h-5 text-brand-gold flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-57" />
                    </button>

                    <AnimatePresence data-id="element-58">
                      {isOpen && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-59">
                          <div className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-60">
                            {faq.answer}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </motion.div>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}